import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "./Navbar";
import "./SignIn.css";

/* ================= API BASE ================= */
const normalizeBase = (value) =>
  String(value || "").trim().replace(/\/+$/, "");

const ensureApiSuffix = (base) => {
  const clean = normalizeBase(base);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiSuffix(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ================= VALIDATION ================= */
const validatePassword = (password = "") => {
  if (!password) return "Password is required.";
  if (password.length < 6) return "Password must be at least 6 characters.";
  return "";
};

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError("");
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setMessage("");

    const safeToken = String(token || "").trim();

    if (!safeToken) {
      setError("Reset token is missing. Please request a new reset link.");
      return;
    }

    const passwordError = validatePassword(formData.password);

    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (!formData.confirmPassword) {
      setError("Confirm password is required.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Password and confirm password do not match.");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE}/auth/reset-password/${encodeURIComponent(safeToken)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            password: formData.password,
            confirmPassword: formData.confirmPassword,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === false) {
        setError(data?.message || "Unable to reset password.");
        return;
      }

      setMessage(data?.message || "Password reset successfully. Redirecting...");

      setFormData({
        password: "",
        confirmPassword: "",
      });

      setTimeout(() => {
        navigate("/signin", { replace: true });
      }, 1200);
    } catch (err) {
      console.error("Reset password error:", err);
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <section className="signin-section">
        <div className="signin-container">
          <div className="signin-left">
            <h1>Create New Password 🔐</h1>

            <p>
              Enter your new password below. After successful reset, you can
              sign in again using your updated password.
            </p>

            <div className="signin-features">
              <div>✔ Secure password update</div>
              <div>✔ Token-verified reset</div>
              <div>✔ Login with new password</div>
            </div>
          </div>

          <div className="signin-card">
            <h2>Reset Password</h2>

            {error ? <div className="auth-alert auth-error">{error}</div> : null}
            {message ? (
              <div className="auth-alert auth-success">{message}</div>
            ) : null}

            <form onSubmit={handleSubmit} className="signin-form">
              <div className="signin-input-group">
                <label htmlFor="reset-password">New Password</label>

                <input
                  id="reset-password"
                  type="password"
                  name="password"
                  placeholder="Enter new password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <div className="signin-input-group">
                <label htmlFor="reset-confirm-password">Confirm Password</label>

                <input
                  id="reset-confirm-password"
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <button type="submit" className="signin-btn" disabled={loading}>
                {loading ? "Resetting Password..." : "Reset Password"}
              </button>
            </form>

            <div className="signin-extra">
              <span>Back to login?</span>
              <Link to="/signin">Sign In</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default ResetPassword;